import { plural, t } from '@lingui/core/macro';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Stack } from '@mui/material';
import { useGetUserQuery, useIndexQuery, useStatisticsQuery, useSuspenseIndexQuery } from 'api';
import { CalendarIcon } from 'components/icons/CalendarIcon';
import { GroupsIconMUI } from 'components/icons/GroupsIconMUI';
import { OfferIconMUI } from 'components/icons/OfferIconMUI';
import { OpenIconMUI } from 'components/icons/OpenIconMUI';
import { SmsIconMUI } from 'components/icons/SmsIconMUI';
import { WarningIconMUI2 } from 'components/icons/WarningIconMUI2';
import { NewBadge } from 'components/NewBadge';
import { NavSection } from 'components/nav-section/NavSection';
import type { NavListProps } from 'components/nav-section/type';
import SvgIcon from 'components/SvgIcon';
import { useCurrentPharmacyOrThrow, useCurrentRole } from 'hooks/slices';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
import { PharmacySearch } from 'layouts/dashboard/navbar/PharmacySearch';
import { useSettingsStatuses } from 'pages/pharmacy/Settings/useSettingsStatuses';
import { useAuthStore } from 'stores/authStore';
import { truthy } from 'utils/arrayUtils';
import { TrackedActions, useTrackEvent } from 'utils/tracking';

export function PharmacyNavSection() {
  const { isCollapse } = useCollapseDrawer();
  const authToken = useAuthStore((state) => state.userToken);
  const { is_lab_chat_enabled: isLabChatEnabled } = useCurrentPharmacyOrThrow();
  const currentRole = useCurrentRole();
  const isResourceAdmin = currentRole && currentRole.kind === 'pharmacy_owner';
  const trackEvent = useTrackEvent();

  const { actionCount: settingsActionCount } = useSettingsStatuses();

  const { data: awaitingActions } = useIndexQuery(
    'pharmacies/trades/participations/_count_awaiting_actions',
    undefined,
    { enabled: true }
  );

  const { data: pharmacySettingsData } = useIndexQuery('pharmacies/pharmacies/current/setting', {});

  const { data: pharmacyCaseStatisticsData } = useStatisticsQuery(
    'pharmacies/cases',
    {},
    { enabled: true }
  );

  const { data: userInfoData } = useGetUserQuery({ enabled: !!authToken });

  const notSeenCount = pharmacyCaseStatisticsData?.statistics.unseen_cases_count;
  const notSeenMessagesDiscussionCount = userInfoData?.meta?.not_seen_discussions_count;
  const isCentralizedCompensationTrackingEnabled =
    pharmacySettingsData?.pharmacy_setting.is_centralized_compensation_tracking_enabled;
  const isKopilotInvoiceEnabled = pharmacySettingsData?.pharmacy_setting.is_real_invoice_enabled;

  const { data: kopilotInvoicesCountsData } = useIndexQuery(
    'pharmacies/kopilot/invoices/counts',
    { kind: 'invoice' },
    { enabled: !!isKopilotInvoiceEnabled }
  );

  const kopilotInvoicesToBeVerifiedCount = kopilotInvoicesCountsData?.counts.to_be_verified;

  const myGroupQuery = useSuspenseIndexQuery('pharmacies/my_group', {});
  const group = myGroupQuery.data.group;

  const navItems: NavListProps[] = [
    {
      title: t`Notes de RDV`,
      to: '/pharmacy/appointment_notes' as const,
      icon: <CalendarIcon />,
      rightIcon: <NewBadge />,
    },
    {
      title: t`Opérations`,
      to: '/pharmacy/trades' as const,
      icon: <OfferIconMUI />,
      ...(awaitingActions?.count && {
        badgeCount: awaitingActions.count,
        'aria-label': `${t`Opérations`}, ${plural(awaitingActions.count, {
          one: '# action',
          other: '# actions',
        })}`,
      }),
    },
    {
      title: t`Réclamations`,
      to: '/pharmacy/claims' as const,
      icon: <WarningIconMUI2 />,
      ...(notSeenCount && { badgeCount: notSeenCount }),
    },
    isLabChatEnabled && {
      title: t`Discussions`,
      to: '/pharmacy/labs_discussions' as const,
      icon: <SmsIconMUI />,
      ...(notSeenMessagesDiscussionCount && { badgeCount: notSeenMessagesDiscussionCount }),
    },
    {
      title: t`Laboratoires`,
      to: '/pharmacy/labs' as const,
      icon: <SvgIcon name="ic_lab" />,
    },
    isCentralizedCompensationTrackingEnabled && {
      title: t`Compensations`,
      to: '/pharmacy/compensations' as const,
      icon: <SvgIcon name="ic_euro" />,
      tooltip: !isResourceAdmin
        ? t`Le module de suivi des compensations est disponible uniquement aux utilisateurs ayant un rôle de titulaire sur Faks. Veuillez contacter votre titulaire pour changer de rôle.`
        : '',
      disabled: !isResourceAdmin,
    },
    isKopilotInvoiceEnabled && {
      title: t`Achats`,
      to: '/pharmacy/kopilot/invoices' as const,
      icon: <ReceiptIcon />,
      rightIcon: <NewBadge />,
      ...(kopilotInvoicesToBeVerifiedCount && { badgeCount: kopilotInvoicesToBeVerifiedCount }),
    },
    {
      title: t`Paramètres`,
      to: '/pharmacy/settings' as const,
      icon: <SvgIcon name="ic_settings" />,
      ...(settingsActionCount > 0 && {
        badgeCount: settingsActionCount,
        'aria-label': `${t`Paramètres`}, ${plural(settingsActionCount, {
          one: '# action à faire',
          other: '# actions à faire',
        })}`,
      }),
    },
    {
      title: 'Aide',
      icon: <SvgIcon name="ic_question" />,
      href: 'https://intercom.help/faks/fr',
    },
    group &&
      group.external_links.length > 0 && {
        hasDivider: true,
        title: t`Modules groupement`,
        icon: <GroupsIconMUI />,
        children: group.external_links.map((link) => ({
          title: (
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
              {link.title}
              <OpenIconMUI fontSize="inherit" />
            </Stack>
          ),
          href: link.url,
          onClick: () => {
            trackEvent(TrackedActions.Clicks.clickExternalLink, {
              groupId: group.id,
              groupName: group.name,
              linkTitle: link.title,
              linkUrl: link.url,
            });
          },
        })),
      },
  ].filter(truthy);

  return (
    <>
      {!isCollapse && <PharmacySearch />}
      <NavSection navItems={navItems} />
    </>
  );
}
